/**
 * Разбор файла с семантикой клиента. Целиком в браузере.
 *
 * Серверного фронтенда у кабинета нет, а в контракте API нет ни одной ручки,
 * принимающей файл: наверх уходит обычный json со списком строк. Значит читать
 * файл здесь, и здесь же прощать клиенту всё, что он может сделать не так.
 *
 * ЗАВИСИМОСТЕЙ НЕТ И НЕ ЗАВОДИТСЯ. В кабинете сейчас ровно две внешние
 * библиотеки — react и роутер, — и тянуть третью, тяжёлую, ради одной кнопки
 * значит платить за неё каждым открытием кабинета у всех клиентов, включая тех,
 * кто ничего не загружает. Эксель внутри — зип с иксэмэлем, разжатие в браузере
 * встроено (`DecompressionStream`), разбор иксэмэля тоже (`DOMParser`).
 *
 * Что прощаем:
 *
 *   * разделители — запятая, точка с запятой, табуляция, просто перевод строки;
 *   * кодировки — utf-8 и windows-1251 (в неё сохраняет csv русский Эксель);
 *   * СТОЛБЕЦ ЛЮБОЙ. Первый пустой, семантика во втором, рядом частотность —
 *     берётся тот столбец, где действительно лежат фразы;
 *   * строка заголовка узнаётся и не попадает в ядро;
 *   * операторы Вордстата и Кей Коллектора (`"`, `!`, `+`, `[]`) снимаются:
 *     клиент выгружает как есть, а движку они ничего не значат.
 *
 * Чего НЕ прощаем и говорим словами: старый двоичный `.xls`, файл больше пяти
 * мегабайт и строку короче восьми знаков — её не принимает сама база
 * (`questions_text_len`), и молча выбросить её нельзя.
 */

/** Потолок файла. Пять мегабайт — это сотни тысяч строк, вчетверо больше любого ядра. */
export const MAX_FILE_BYTES = 5 * 1024 * 1024;

/** Сколько строк вообще разбираем: дальше начинается не семантика, а выгрузка базы. */
export const MAX_ROWS = 20000;

/** Ниже этой длины строку не примет база, и это не наше правило, а её ограничение. */
export const MIN_TEXT_LENGTH = 8;

/**
 * Выше этой длины строку не примет контракт (`QuestionCreate.text`).
 *
 * Считать её здесь обязательно: тело запроса проверяется целиком, и одна строка
 * в тысячу первый знак отвергает ВСЮ пачку отказом проверки. Клиент при этом
 * увидит «не загрузилось ничего» и не узнает, какая из пятисот строк виновата.
 */
export const MAX_TEXT_LENGTH = 1000;

/** Разделители, которые встречаются в выгрузках. Порядок не важен: победит частый. */
const DELIMITERS = [',', ';', '\t'];

/** Слова, которыми называют столбец. Строка из них — заголовок, а не семантика. */
const HEADER_WORDS = [
  'фраза',
  'фразы',
  'запрос',
  'запросы',
  'ключевое слово',
  'ключевые слова',
  'ключевик',
  'семантика',
  'вопрос',
  'вопросы',
  'частотность',
  'частота',
  'показы',
  'конкуренция',
  'keyword',
  'keywords',
  'query',
  'phrase',
  'volume',
];

export interface ParsedSemantics {
  /** Готовые к загрузке фразы: обрезанные, очищенные, без повторов. */
  rows: string[];
  /** Какой столбец взяли, считая с единицы. Ноль — файл был одностолбцовым. */
  column: number;
  /** Сколько столбцов вообще нашлось: об этом стоит сказать, если их больше одного. */
  columns: number;
  /** Снятая строка заголовка, если она была. */
  header: string | null;
  /** Сколько повторов схлопнули. */
  duplicates: number;
  /** Строки короче восьми знаков: их не примет база, и о них говорим отдельно. */
  tooShort: string[];
  /** Строки длиннее тысячи знаков: их не примет контракт, и молчать о них нельзя. */
  tooLong: string[];
  /** Разобрано ли всё: на потолке строк остаток остаётся в файле. */
  truncated: boolean;
}

/** Файл, который мы читать не умеем или не станем. */
export class SemanticsError extends Error {}

function normalized(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLowerCase();
}

/**
 * Снять операторы поисковых сервисов и лишние кавычки.
 *
 * `"!купить +диван [москва]"` — это язык Вордстата, а не речь покупателя.
 * Движку эти знаки не значат ничего, а в ядре они остались бы навсегда.
 */
function cleaned(value: string): string {
  return value
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^["'«»[\]]+|["'«»[\]]+$/g, '')
    .replace(/(^|\s)[!+]+(?=\S)/g, '$1')
    .trim();
}

function looksLikeHeader(value: string): boolean {
  const text = normalized(value);
  return HEADER_WORDS.some((word) => text === word || text.startsWith(`${word} `));
}

/**
 * Похоже ли это на фразу, а не на число, ссылку или служебную метку.
 *
 * По этой мерке выбирается столбец: в выгрузке рядом с фразами почти всегда
 * стоят частотность и конкуренция, и без разбора «что тут вообще текст» продукт
 * загрузил бы в ядро колонку чисел.
 */
function looksLikePhrase(value: string): boolean {
  const text = value.trim();
  if (text.length < 3) return false;
  if (/^[\d\s.,%-]+$/.test(text)) return false;
  if (/^https?:\/\//i.test(text)) return false;
  return /[\p{L}]/u.test(text);
}

/** Разбить csv или tsv на матрицу, уважая кавычки. */
function splitRows(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const symbol = text[index];
    if (quoted) {
      if (symbol === '"') {
        // Удвоенная кавычка внутри поля — это одна кавычка, а не конец поля.
        if (text[index + 1] === '"') {
          cell += '"';
          index += 1;
        } else {
          quoted = false;
        }
      } else {
        cell += symbol;
      }
      continue;
    }
    if (symbol === '"') {
      quoted = true;
    } else if (symbol === delimiter) {
      row.push(cell);
      cell = '';
    } else if (symbol === '\n') {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else if (symbol !== '\r') {
      cell += symbol;
    }
  }
  row.push(cell);
  rows.push(row);
  return rows;
}

/**
 * Правда ли запятая делит файл на столбцы, а не стоит внутри фраз.
 *
 * ЗАПЯТАЯ — ЕДИНСТВЕННЫЙ РАЗДЕЛИТЕЛЬ, КОТОРЫЙ ЖИВЁТ ВНУТРИ САМОЙ ФРАЗЫ. Список
 * «купить диван, недорого» и «заказать стол, дёшево» делится ею ровно, по одной
 * запятой на строку, и одной ровности хватило бы, чтобы объявить его таблицей из
 * двух столбцов и записать в ядро половинки фраз. Молча, без единого числа на
 * экране: клиент увидел бы «нашли 2 фразы» и не понял, что они обрезаны.
 *
 * Точка с запятой и табуляция внутри запросов не встречаются, им довольно
 * ровности. Запятой нужно доказательство того, что рядом с фразами лежит НЕ
 * речь: столбец чисел (частотность, конкуренция) или узнанная строка заголовка.
 */
function commaSplitsColumns(rows: string[][]): boolean {
  // Строка заголовка — та, где ЗАГОЛОВОК КАЖДАЯ ячейка, а не какая-нибудь одна.
  // «Фраза;Частотность» — заголовок. «вопрос, который задать продавцу» — фраза,
  // начинающаяся словом из списка, и принять её за шапку значит разрезать по
  // запятой весь файл из-за одной первой строки.
  const first = (rows[0] ?? []).map((cell) => cell.trim()).filter(Boolean);
  if (first.length > 1 && first.every(looksLikeHeader)) return true;
  const width = rows.reduce((most, row) => Math.max(most, row.length), 0);
  for (let index = 1; index < width; index += 1) {
    const values = rows.map((row) => (row[index] ?? '').trim());
    const filled = values.filter(Boolean);
    // Столбец считается доводом, только если он ЗАПОЛНЕН. Пустые ячейки — это
    // строки без запятой вовсе, и без этого условия список, где запятая стоит в
    // одной строке из трёх, выглядел бы таблицей: два значения из трёх пусты,
    // «не фразы», довод найден, фразы разрезаны пополам.
    if (filled.length * 2 < values.length) continue;
    if (filled.filter(looksLikePhrase).length * 2 < filled.length) return true;
  }
  return false;
}

/**
 * Выбрать разделитель по тому, какой из них РОВНЕЕ делит файл.
 *
 * Считать вхождения мало: в русских фразах запятых больше, чем столбцов, и по
 * их числу победила бы запятая в файле с точками с запятой. Ровным считается
 * тот, у которого число полей от строки к строке одно и то же.
 */
function pickDelimiter(text: string): string {
  const sample = text.split('\n').slice(0, 50).join('\n');
  let best = '\n';
  let bestScore = 0;
  for (const delimiter of DELIMITERS) {
    const rows = splitRows(sample, delimiter).filter((row) =>
      row.some((cell) => cell.trim()),
    );
    if (rows.length === 0) continue;
    const widths = rows.map((row) => row.length);
    const width = Math.max(...widths);
    if (width < 2) continue;
    if (delimiter === ',' && !commaSplitsColumns(rows)) continue;
    const even = widths.filter((value) => value === width).length / widths.length;
    const score = even * width;
    if (score > bestScore) {
      best = delimiter;
      bestScore = score;
    }
  }
  return best;
}

/**
 * Прочитать текст файла, угадав кодировку.
 *
 * utf-8 проверяется строгим декодером: он падает на первом же байте не своей
 * кодировки, и это единственный честный способ отличить её от windows-1251, в
 * которой русский Эксель сохраняет csv по умолчанию.
 */
export function decodeText(buffer: ArrayBuffer): string {
  try {
    return new TextDecoder('utf-8', { fatal: true })
      .decode(buffer)
      .replace(/^\uFEFF/, '');
  } catch {
    return new TextDecoder('windows-1251').decode(buffer);
  }
}

/**
 * Свести матрицу к списку фраз: выбрать столбец, снять заголовок, убрать повторы.
 */
export function pickColumn(matrix: string[][]): ParsedSemantics {
  const rows = matrix.filter((row) => row.some((cell) => cell.trim()));
  const width = rows.reduce((most, row) => Math.max(most, row.length), 0);
  let column = 0;
  let best = -1;
  for (let index = 0; index < width; index += 1) {
    const values = rows.map((row) => (row[index] ?? '').trim());
    // Столбец выигрывает по числу настоящих фраз, а при равенстве — по их
    // длине: у выгрузки с фразой и её переводом оба столбца полны, и брать надо
    // тот, где текст содержательнее.
    const phrases = values.filter(looksLikePhrase);
    const score =
      phrases.length * 1000 +
      phrases.reduce((sum, value) => sum + Math.min(value.length, 100), 0) / 100;
    if (score > best) {
      best = score;
      column = index;
    }
  }

  const values = rows.map((row) => cleaned(row[column] ?? '')).filter(Boolean);
  const first = values[0] ?? '';
  let header: string | null = null;
  // Заголовок снимается только у таблицы, где под ним что-то есть. Файл из
  // одной строки «Фраза» — это не заголовок, а единственная семантика клиента,
  // и выбрасывать её значит показать пустой разбор без единой причины.
  if (values.length > 1 && looksLikeHeader(first)) {
    header = first;
    values.shift();
  }

  const seen = new Set<string>();
  const kept: string[] = [];
  const tooShort: string[] = [];
  const tooLong: string[] = [];
  let duplicates = 0;
  for (const value of values) {
    const key = normalized(value);
    if (seen.has(key)) {
      duplicates += 1;
      continue;
    }
    seen.add(key);
    if (value.length < MIN_TEXT_LENGTH) {
      tooShort.push(value);
      continue;
    }
    // Длинную строку нельзя ни обрезать, ни отправить. Обрезать — переписать
    // вопрос клиента за него, а отправить — уронить проверкой тела всю пачку
    // разом, вместе с четырьмя сотнями годных строк рядом.
    if (value.length > MAX_TEXT_LENGTH) {
      tooLong.push(value);
      continue;
    }
    kept.push(value);
  }

  return {
    rows: kept.slice(0, MAX_ROWS),
    column: width > 1 ? column + 1 : 0,
    columns: width,
    header,
    duplicates,
    tooShort,
    tooLong,
    truncated: kept.length > MAX_ROWS,
  };
}

/** Разобрать csv, tsv, txt или вставленный из буфера столбец. */
export function parseText(text: string): ParsedSemantics {
  const delimiter = pickDelimiter(text);
  return pickColumn(
    delimiter === '\n'
      ? text.split('\n').map((line) => [line])
      : splitRows(text, delimiter),
  );
}

// ---------------------------------------------------------------------------
// Эксель
// ---------------------------------------------------------------------------

const ZIP_CENTRAL = 0x02014b50;
const ZIP_END = 0x06054b50;

interface ZipEntry {
  name: string;
  method: number;
  size: number;
  offset: number;
}

/** Прочитать оглавление зипа: без него размеры в местных заголовках бывают нулями. */
function readDirectory(view: DataView): ZipEntry[] {
  let end = -1;
  for (let index = view.byteLength - 22; index >= 0; index -= 1) {
    if (view.getUint32(index, true) === ZIP_END) {
      end = index;
      break;
    }
  }
  if (end < 0) throw new SemanticsError('not-a-zip');
  const entries: ZipEntry[] = [];
  const count = view.getUint16(end + 10, true);
  let offset = view.getUint32(end + 16, true);
  // Зип64 узнаётся по заглушкам на месте числа файлов и смещения оглавления:
  // настоящие значения лежат в отдельной записи, которую мы не читаем. Приняв
  // заглушку за число, разбор ушёл бы за конец файла и упал бы «файл не
  // прочитался» — отказ верный, но причина в нём названа неправильно.
  if (count === 0xffff || offset === 0xffffffff) throw new SemanticsError('zip64');
  const decoder = new TextDecoder('utf-8');
  for (let index = 0; index < count; index += 1) {
    if (view.getUint32(offset, true) !== ZIP_CENTRAL) break;
    const method = view.getUint16(offset + 10, true);
    const size = view.getUint32(offset + 20, true);
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const local = view.getUint32(offset + 42, true);
    const name = decoder.decode(
      new Uint8Array(view.buffer, view.byteOffset + offset + 46, nameLength),
    );
    entries.push({ name, method, size, offset: local });
    offset += 46 + nameLength + extraLength + commentLength;
  }
  return entries;
}

async function readEntry(buffer: ArrayBuffer, entry: ZipEntry): Promise<string> {
  const view = new DataView(buffer);
  const nameLength = view.getUint16(entry.offset + 26, true);
  const extraLength = view.getUint16(entry.offset + 28, true);
  const start = entry.offset + 30 + nameLength + extraLength;
  const data = new Uint8Array(buffer, start, entry.size);
  if (entry.method === 0) return new TextDecoder('utf-8').decode(data);
  if (entry.method !== 8) throw new SemanticsError('unsupported-compression');
  // Разжатие в браузере встроенное, но не вечное: до 2023 года его не было. Без
  // проверки клиент на старом браузере получил бы «файл не прочитался» и стал бы
  // пересохранять исправный файл, вместо того чтобы открыть кабинет в другом.
  if (typeof DecompressionStream === 'undefined') throw new SemanticsError('old-browser');
  // `deflate-raw` — без заголовка zlib, ровно то, что лежит в зипе.
  const stream = new Blob([data])
    .stream()
    .pipeThrough(new DecompressionStream('deflate-raw'));
  return new Response(stream).text();
}

/**
 * Разобрать иксэмэль книги.
 *
 * Коллекции узлов ниже везде оборачиваются в `Array.from`, а не перебираются
 * напрямую: по стандарту `HTMLCollection` перебираемая, но это единственное
 * место кабинета, которое ходит по узлам иксэмэля, и полагаться здесь на
 * тонкость реализации незачем — цена нулевая, а поломка была бы у клиента.
 */
function xml(text: string): Document {
  const document_ = new DOMParser().parseFromString(text, 'application/xml');
  // Разбор иксэмэля не бросает: на битой разметке он возвращает документ с
  // `parsererror` внутри. Не проверив, мы прочитали бы повреждённую книгу как
  // пустую и показали бы «нашли 0 фраз» вместо «файл не прочитался».
  if (document_.getElementsByTagName('parsererror').length > 0) {
    throw new SemanticsError('not-a-workbook');
  }
  return document_;
}

/** Текст ячейки общего словаря: он бывает разбит на куски разного начертания. */
function sharedStrings(document_: Document): string[] {
  return Array.from(document_.getElementsByTagName('si'), (item) =>
    Array.from(item.getElementsByTagName('t'), (part) => part.textContent ?? '').join(''),
  );
}

/** Номер столбца из имени ячейки: `B7` — это второй столбец. */
function columnOf(reference: string): number {
  const letters = reference.replace(/\d+/g, '');
  let index = 0;
  for (const letter of letters) index = index * 26 + (letter.charCodeAt(0) - 64);
  return Math.max(index - 1, 0);
}

/**
 * Найти лист, который человек видит первым.
 *
 * Не `sheet1.xml`: порядок файлов в архиве не обязан совпадать с порядком
 * вкладок, и у книги, где первую вкладку добавили позже, взялся бы чужой лист.
 * Порядок объявляет книга, а имя файла — её связи.
 */
function firstSheetPath(workbook: Document, rels: Document): string {
  const sheet = workbook.getElementsByTagName('sheet')[0];
  const id = sheet?.getAttribute('r:id') ?? '';
  for (const relation of Array.from(rels.getElementsByTagName('Relationship'))) {
    if (relation.getAttribute('Id') === id) {
      const target = relation.getAttribute('Target') ?? '';
      return target.startsWith('/')
        ? target.slice(1)
        : `xl/${target.replace(/^\.\//, '')}`;
    }
  }
  return 'xl/worksheets/sheet1.xml';
}

/** Разобрать книгу Экселя: первый лист, все столбцы, дальше общий разбор. */
export async function parseWorkbook(buffer: ArrayBuffer): Promise<ParsedSemantics> {
  const entries = readDirectory(new DataView(buffer));
  const find = (name: string) => entries.find((entry) => entry.name === name);
  const workbookEntry = find('xl/workbook.xml');
  const relsEntry = find('xl/_rels/workbook.xml.rels');
  if (!workbookEntry || !relsEntry) throw new SemanticsError('not-a-workbook');
  const path = firstSheetPath(
    xml(await readEntry(buffer, workbookEntry)),
    xml(await readEntry(buffer, relsEntry)),
  );
  const sheetEntry = find(path);
  if (!sheetEntry) throw new SemanticsError('not-a-workbook');
  const stringsEntry = find('xl/sharedStrings.xml');
  const strings = stringsEntry
    ? sharedStrings(xml(await readEntry(buffer, stringsEntry)))
    : [];

  const sheet = xml(await readEntry(buffer, sheetEntry));
  const matrix: string[][] = [];
  for (const row of Array.from(sheet.getElementsByTagName('row'))) {
    const cells: string[] = [];
    for (const cell of Array.from(row.getElementsByTagName('c'))) {
      const index = columnOf(cell.getAttribute('r') ?? '');
      const kind = cell.getAttribute('t');
      let value = '';
      if (kind === 's') {
        value =
          strings[Number(cell.getElementsByTagName('v')[0]?.textContent ?? '-1')] ?? '';
      } else if (kind === 'inlineStr') {
        value = Array.from(
          cell.getElementsByTagName('t'),
          (part) => part.textContent ?? '',
        ).join('');
      } else {
        value = cell.getElementsByTagName('v')[0]?.textContent ?? '';
      }
      while (cells.length < index) cells.push('');
      cells[index] = value;
    }
    matrix.push(cells);
    if (matrix.length >= MAX_ROWS + 1) break;
  }
  return pickColumn(matrix);
}

/**
 * Разобрать то, что человек принёс: книгу Экселя или текстовый файл.
 *
 * Двоичный `.xls` — это не зип, и притворяться, что мы его не узнали, нечестно:
 * у отказа своя причина, и она называется словами на экране.
 */
export async function parseFile(file: File): Promise<ParsedSemantics> {
  if (file.size > MAX_FILE_BYTES) throw new SemanticsError('too-big');
  const buffer = await file.arrayBuffer();
  const head = new Uint8Array(buffer, 0, Math.min(2, buffer.byteLength));
  const zipped = head[0] === 0x50 && head[1] === 0x4b;
  if (zipped) return parseWorkbook(buffer);
  if (/\.xlsx?$/i.test(file.name)) throw new SemanticsError('old-excel');
  return parseText(decodeText(buffer));
}
